import React from 'react'
import { Helmet } from 'react-helmet';
import AppLayoutTitle from './appLayoutTitle';
import ComTable from '../components/comTable';
import Swal from 'sweetalert2';
import { apiGet, apiPost } from '../../helper/helperApi';
import { handleException } from '../../helper/helperError';


const AppDomains = () => {
    const [dataset, setDataset] = React.useState(undefined);

    const fetchData = async () => {
        try {
            apiGet("/domains")
                .then((res) => { setDataset(res.data || []); })
                .catch((ex) => handleException(ex, "Failed to fetch domains data"));
        } catch (ex) {
            handleException(ex, "Failed to fetch domains data!");
        }
    }

    React.useEffect(() => {
        fetchData();
    }, []);

    const handleAddDomain = async () => {
        const verificationToken = await apiGet("/domains/verification-token")
        const txtName = 'sparkling-email-verification';
        const txtValue = verificationToken?.data?.token;
        if (!txtValue) return handleException(null, "Failed to fetch verification token");

        Swal.fire({
            title: 'Add Domain',
            html: `
                <ol style="text-align:left; font-size:14px; line-height:1.6;">
                    <li>Go to your domain's DNS settings</li>
                    <li>Create a new <strong>TXT</strong> record with the following details:</li>
                </ol>
                <div style="background:#f4f4f4; padding:10px; border-radius:4px; font-size:13px; text-align:left; line-height:2;">
                    Type: <strong>TXT</strong><br/>
                    Name: <strong>${txtName}</strong>
                    <button 
                        onclick="navigator.clipboard.writeText('${txtName}')" 
                        title="Copy Name" 
                        tabindex="-1"
                        class="btn btn-sm btn-outline-secondary"
                        style="padding: 3px 6px;"
                    >
                        <i class="fa fa-copy"></i>
                    </button>
                    <br/>
                    Value: <strong>${txtValue}</strong>
                    <button
                        onclick="navigator.clipboard.writeText('${txtValue}')" 
                        title="Copy Value" 
                        tabindex="-1"
                        class="btn btn-sm btn-outline-secondary"
                        style="padding: 3px 6px;"
                    >
                        <i class="fa fa-copy"></i>
                    </button>
                    <br/>
                </div>
                <input id="swal-input-domain" class="swal2-input" placeholder="yourdomain.com" />`,
            width: "720px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Check DNS Record',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            preConfirm: () => {
                const domain = document.getElementById('swal-input-domain').value;
                if (!domain) Swal.showValidationMessage(`Please enter a domain`);
                return { domain: domain };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const domain = result.value.domain;
                apiPost("/domains/new", { domain: domain })
                    .then((res) => {
                        if (res?.data?.success === true) {
                            Swal.fire({
                                title: 'Domain Successfully Added!',
                                text: 'Your domain has been added.',
                                icon: 'success',
                                timer: 1500
                            })
                        }
                        else {
                            Swal.fire({
                                title: 'Error Adding Domain',
                                text: res?.data?.message || "Failed to add domain",
                                icon: 'error',
                                timer: 1500
                            })
                        }
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to add domain");
                        fetchData();
                    })
            }
        });
    }

    const handleVerify = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid domain data");
        const verificationToken = await apiGet("/domains/verification-token")
        const txtName = 'sparkling-email-verification';
        const txtValue = verificationToken?.data?.token;
        if (!txtValue) return handleException(null, "Failed to fetch verification token");

        Swal.fire({
            title: 'Verify Domain',
            html: `
  <div style="text-align: left; font-size: 14px; line-height: 1.6;">
    <p>To verify your domain, follow these steps:</p>
    <ol style="padding-left: 16px;">
      <li>Go to your DNS provider's dashboard</li>
      <li>Create a <strong>TXT</strong> record with the following:</li>
    </ol>
    <div style="background: #f8f8f8; padding: 10px; border-radius: 6px; font-size: 13px; line-height: 2.0; margin-bottom: 10px;">
      <div><strong>Type:</strong> TXT</div>
      <div>
        <strong>Name:</strong>
        <span id="txt-name" style="word-break: break-all;">${txtName}</span>
        <button 
            onclick="navigator.clipboard.writeText('${txtName}')" 
            title="Copy Name" 
            tabindex="-1"
            class="btn btn-sm btn-outline-secondary"
            style="padding: 3px 6px;"
        >
            <i class="fa fa-copy"></i>
        </button>
      </div>
      <div>
        <strong>Value:</strong>
        <span id="txt-value" style="word-break: break-all;">${txtValue}</span>
        <button 
            onclick="navigator.clipboard.writeText('${txtValue}')" 
            title="Copy Value" 
            tabindex="-1"
            class="btn btn-sm btn-outline-secondary"
            style="padding: 3px 6px;"
        >
            <i class="fa fa-copy"></i>
        </button>
      </div>
    </div>
  </div>`,
            width: "720px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Verify Domain',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
        }).then((result) => {
            if (result.isConfirmed) {
                apiPost(`/domains/${row.id}/verify`)
                    .then((res) => {
                        if (res?.data?.success === true) {
                            Swal.fire({
                                title: 'Domain Successfully Verified!',
                                text: 'Your domain has been verified.',
                                icon: 'success',
                                timer: 1500
                            })
                        }
                        else {
                            Swal.fire({
                                title: 'Error Verifying Domain',
                                text: res?.data?.message || "Failed to verify domain",
                                icon: 'error',
                                timer: 1500
                            })
                        }
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to verify domain");
                        fetchData();
                    })
            }
        });
    }

    const handleDeactivate = (row) => {
        if (!row || !row.id) return handleException(null, "Invalid domain data");
        Swal.fire({
            title: 'Deactivate Domain',
            text: `Are you sure you want to deactivate this domain?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Deactivate Domain',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
        }).then((result) => {
            if (result.isConfirmed) {
                apiPost(`/domains/${row.id}/deactivate`)
                    .then(() => {
                        Swal.fire({
                            title: 'Deactivated!',
                            text: 'Your domain has been deactivated.',
                            icon: 'success',
                            timer: 1500
                        })
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to deactivate domain");
                        fetchData();
                    })
            }
        });
    }

    const handleReactivate = (row) => {
        if (!row || !row.id) return handleException(null, "Invalid domain data");
        Swal.fire({
            title: 'Reactivate Domain',
            text: `Are you sure you want to reactivate this domain?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Reactivate Domain',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
        }).then((result) => {
            if (result.isConfirmed) {
                apiPost(`/domains/${row.id}/reactivate`)
                    .then(() => {
                        Swal.fire({
                            title: 'Reactivated!',
                            text: 'Your domain has been reactivated.',
                            icon: 'success',
                            timer: 1500
                        })
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to reactivate domain");
                        fetchData();
                    })
            }
        });
    }

    const handleDelete = (row) => {
        if (!row || !row.id) return handleException(null, "Invalid domain data");
        Swal.fire({
            title: 'Delete Domain',
            text: `Are you sure you want to delete this domain?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete Domain',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
        }).then((result) => {
            if (result.isConfirmed) {
                apiPost(`/domains/${row.id}/delete`)
                    .then(() => {
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Your domain has been deleted.',
                            icon: 'success',
                            timer: 1500
                        })
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to delete domain");
                        fetchData();
                    })
            }
        });
    }


    return <>
        <Helmet>
            <title>Domains | Sparkling Email</title>
        </Helmet>
        <>
            <AppLayoutTitle title="Domains" buttons={
                <button className="btn btn-gold text-dark" onClick={handleAddDomain}>Add Domain</button>
            } />
            <div className="px-3 py-3">
                <div>
                    Easily add and manage domains to customize your email sending flow.
                </div>
                <div>
                    Add your sending domains here to set up and verify ownership for email delivery.
                    Domains can be used to support different languages, workflows, or clients—each with its
                    own tracking and reporting.
                </div>
                <br />
                <ComTable
                    handleEdit={(row) => handleEdit(row)}
                    handleVerify={(row) => handleVerify(row)}
                    handleDeactivate={(row) => handleDeactivate(row)}
                    handleReactivate={(row) => handleReactivate(row)}
                    handleDelete={(row) => handleDelete(row)}
                    data={dataset}
                    isUndefinedIsLoading={true}
                />
                {(dataset !== undefined && Array.isArray(dataset) && dataset.length === 0) &&
                    <div className="text-center my-5">
                        <button className="btn btn-lg btn-outline-secondary"
                            onClick={handleAddDomain}>Add First Domain</button>
                    </div>
                }
            </div>
        </>
    </>
}
export default AppDomains;